import { Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  ButtonIcon,
  FileUpload,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import {
  deleteUploadedFile,
  getSignedPrivateUploadUrl
} from '../../../../../../api/queries';
import { FieldLabel } from '../../../../../../components/Field';
import { getFileExtension, openFile } from '../../../../../../utils/file';
import axiosInstance from '../../../../../../api/client';
import { AxiosProgressEvent } from 'axios';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import {
  AllowedFileFormats,
  FormFieldSupplementary
} from '@publishing-engine/domain/types/article';
import { get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useDuplicateConfirm } from 'src/hooks/useDuplicateConfirm';

interface Props {
  submissionId: string;
  config: FormFieldSupplementary;
}

export type ISupplementaryUpload = {
  fileSet: {
    Supplementary: ISubmissionFile[];
  };
};

const MAX_FILES_AT_ONCE = 15;
const formatLabel = (ext: string) => ext.replace('.', '').toUpperCase();
export const AllowedFiles = ({
  allowedFile
}: {
  allowedFile: AllowedFileFormats;
}) => {
  const allExtensions = Array.from(new Set(Object.values(allowedFile).flat()));

  const visible = allExtensions.slice(0, 5);
  const hidden = allExtensions.slice(5);

  return (
    <>
      Accepted format(s):{' '}
      {visible.map((ext, index) => (
        <Text as="span" key={ext}>
          {formatLabel(ext)}
          {index < visible.length - 1 && ', '}
        </Text>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          label={hidden.map(formatLabel).join(', ')}
          hasArrow
          borderRadius={4}
          shouldWrapChildren
          placement="top"
        >
          <Text cursor="pointer" ml={1} as="span" color="blue">
            View More
          </Text>
        </Tooltip>
      )}
    </>
  );
};

const SupplementaryUpload = ({ submissionId, config }: Props) => {
  const toast = useToast();
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    register,
    getValues
  } = useFormContext<ISupplementaryUpload>();
  const {
    caseDataFields: { main: field },
    params: main
  } = config;

  const mainError = get(errors, field);
  const { checkIsDuplicateFile, duplicateFileModal } = useDuplicateConfirm();
  const { append, remove, update, fields } = useFieldArray({
    control,
    name: field
  });

  const getUploadUrl = (extension: string) => {
    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const uploadFile = async (file: File, index: number) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: ISubmissionFile = {
      name: file.name,
      extension: fileExtension,
      progress: 0,
      isUploading: true,
      url: '',
      referenceName: file.name
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      update(index, fileToUpload);
    };

    try {
      append(fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        extension: fileExtension
      };

      clearErrors(`${field}.${index}`);
      update(index, fileToUpload);
    } catch (e) {
      setError(`${field}.${index}`, {
        type: 'upload-error',
        message: `Upload Error: ${file.name} (${(e as Error).message})`
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  const removeFile = async (index: number, fileId?: string) => {
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);
    let uploadIndex = fields?.length || 0;
    for (const file of acceptedFiles) {
      const canUpload = await checkIsDuplicateFile(
        { fieldName: field, name: file?.name },
        getValues()?.fileSet
      );
      if (canUpload) {
        uploadFile(file, uploadIndex++);
      }
    }
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] =
    async rejectedFiles => {
      if (!rejectedFiles || rejectedFiles.length === 0) return;

      // Separate rejected files into 'too many files' and other errors
      const tooManyFiles = rejectedFiles.filter(r =>
        r.errors.some(e => e.code === 'too-many-files')
      );
      const otherRejected = rejectedFiles.filter(
        r => !r.errors.some(e => e.code === 'too-many-files')
      );

      // Upload only allowed number of files from 'too many files'
      if (tooManyFiles.length > 0 && MAX_FILES_AT_ONCE > 0) {
        const filesToUpload = tooManyFiles
          .slice(0, MAX_FILES_AT_ONCE)
          .map(r => r.file);

        await Promise.all(
          filesToUpload.map((file, index) =>
            uploadFile(file, index + fields.length)
          )
        );
        toast({
          variant: 'attention',
          title: 'File Upload Limit Exceeded',
          description:
            'You can only upload up to ' +
            MAX_FILES_AT_ONCE +
            ' files at once. Excess files were not uploaded.',
          isClosable: true
        });
      }

      // Handle other rejected files (wrong type, too large, etc.)
      if (otherRejected.length > 0) {
        const messages = otherRejected.map(({ file, errors }) => {
          const reasons = errors.map(err => {
            switch (err.code) {
              case 'file-invalid-type':
                return 'Unsupported file type';
              case ErrorCode.FileTooLarge:
                return `File is larger than ${main.validations.maxSizeMB}MB`;
              default:
                return 'Upload rejected';
            }
          });
          return `${file.name}: ${reasons.join(', ')}`;
        });

        setError(field, {
          type: 'upload-rejected',
          message: messages.join(',')
        });
      }
    };

  const disableDeleteButton = () => {
    if (fields.length > 0) {
      return fields.some(file => file.isUploading);
    }
    return false;
  };

  const isDeleteButtonDisabled = disableDeleteButton();

  return (
    <OutlineCard
      width="100%"
      padding={4}
      wordBreak={'break-word'}
      whiteSpace={'normal'}
    >
      <Flex flexDirection="column" gap={2}>
        <Flex flexDirection="column">
          <FieldLabel
            isRequired={main.validations.required}
            label={main.labels.main}
            subText={main.labels.subtitle}
            helpText={main.labels.helpText}
          />
        </Flex>
        <Flex flexDirection="column" w={{ base: '100%', md: '50%' }}>
          <FileUpload
            {...register(field)}
            uploadButtonText="Select file(s)"
            multiple
            onDrop={onDrop}
            onDropRejected={onDropRejected}
            maxFiles={MAX_FILES_AT_ONCE}
            maxSize={main.validations.maxSizeMB * 1000000}
            isInvalid={Boolean(errors.fileSet?.Supplementary)}
            accept={main.validations.allowedFileFormats}
            ariaLabel={'Upload Supplementary Files'}
          />
        </Flex>
        <Text textStyle="textContext">
          <AllowedFiles allowedFile={main.validations.allowedFileFormats} />.
          Max size: {main.validations.maxSizeMB}MB. Executable scripts are not
          permitted.
        </Text>
      </Flex>

      {fields.length ? (
        <Flex flexDirection="column" mt={2} gap={1}>
          {' '}
          {fields.map((file, index) => (
            <Flex key={file.id || file.url} flexDirection="row" width="100%">
              <Flex alignItems="center" gap={2} flexDirection="row">
                {file.isUploading ? (
                  <Text textStyle="textContext">Uploading</Text>
                ) : mainError?.[index] ? (
                  <FormFeedback variant="error">
                    {Object.keys(mainError[index]).length === 1 &&
                    mainError[index].url
                      ? `Upload Error: ${file.referenceName} (Request failed with status code 500)`
                      : mainError[index].message || 'Unknown error'}
                  </FormFeedback>
                ) : (
                  <a
                    onClick={() => openFile(file.url)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FormFeedback variant="success">{file.name}</FormFeedback>
                  </a>
                )}
              </Flex>

              {!file.isUploading && (
                <ButtonIcon
                  size="small"
                  ariaLabel={`${file.name} delete`}
                  onClick={() => removeFile(index, file.id)}
                  disabled={isDeleteButtonDisabled}
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                />
              )}
            </Flex>
          ))}
        </Flex>
      ) : null}
      {mainError?.type === 'upload-rejected' && (
        <Flex flexDirection="column" gap={2}>
          {(mainError?.message?.split(',') as string[]).map((msg, i) => (
            <FormFeedback key={i} variant="error">
              {msg}
            </FormFeedback>
          ))}
        </Flex>
      )}
      {duplicateFileModal}
    </OutlineCard>
  );
};

export default SupplementaryUpload;
